import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useAppSelector } from "@/redux/hooks/hooks";
import {
  useGetBrandDetailsQuery,
  useUpdateBrandDetailsMutation,
} from "@/redux/feature/videoDashboard/VideoDashboardApi";

interface BrandFormData {
  brand: string;
  brands_fb_url: string;
  brands_ig_username: string;
}

interface BrandDetailsModalProps {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const initialForm: BrandFormData = {
  brand: "",
  brands_fb_url: "",
  brands_ig_username: "",
};

const BrandDetailsModal: React.FC<BrandDetailsModalProps> = ({ setIsOpen }) => {
  const [formData, setFormData] = useState<BrandFormData>(initialForm);
  const [errors, setErrors] = useState<Partial<BrandFormData>>({});

  const email = useAppSelector((state) => state.auth.user?.email);

  const { data: brandDetails, isLoading: isLoadingDetails } =
    useGetBrandDetailsQuery(email);
  const [updateBrandDetails, { isLoading: isUpdating }] =
    useUpdateBrandDetailsMutation();

  // Populate form with existing data when brandDetails is loaded
  useEffect(() => {
    if (brandDetails && brandDetails.hasDetails) {
      setFormData({
        brand: brandDetails.brand || "",
        brands_fb_url: brandDetails.brands_fb_url || "",
        brands_ig_username: brandDetails.brands_ig_username || "",
      });
    }
  }, [brandDetails]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name as keyof BrandFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const newErrors: Partial<BrandFormData> = {};

    const urlRegex = /^(https?:\/\/)?([\w\d-]+\.)+[\w\d]{2,}(\/.*)?$/i;
    const instagramUsernameRegex = /^[a-zA-Z0-9._]{1,30}$/;

    if (!formData.brand.trim()) {
      newErrors.brand = "Brand name is required";
    }

    // if (
    //   formData.brands_fb_url.trim() &&
    //   !urlRegex.test(formData.brands_fb_url.trim())
    // ) {
    //   newErrors.brands_fb_url = "Please enter a valid Facebook URL";
    // }

    if (
      formData.brands_ig_username.trim() &&
      !instagramUsernameRegex.test(formData.brands_ig_username.trim())
    ) {
      newErrors.brands_ig_username = "Please enter a valid Instagram username";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validate()) return;

    const payload = {
      email: email,
      brand: formData.brand,
      brands_fb_url: formData.brands_fb_url,
      brands_ig_username: formData.brands_ig_username,
    };

    try {
      await updateBrandDetails(payload).unwrap();
      setIsOpen(false);
      toast.success("Brand details updated successfully!");
    } catch (err) {
      console.error("Update brand details error:", err);
      toast.error("Failed to update brand details");
    }
  };

  if (isLoadingDetails) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-black border-t-transparent"></div>
          <span className="ml-3 text-gray-600">Loading brand details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-black">Brand Details</h2>
        <p className="text-gray-600 text-sm mt-1">
          Update your brand information
        </p>
      </div>

      <div>
        <label className="block text-sm text-black mb-2">
          <span className="font-medium">Brand Name *</span>{" "}
          <span className="italic">( as per facebook profile page)</span>
        </label>
        <input
          type="text"
          name="brand"
          value={formData.brand}
          onChange={handleChange}
          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-1 ${
            errors.brand
              ? "border-red-500 ring-red-500"
              : "border-gray-300 focus:border-black focus:ring-black"
          }`}
          placeholder="Enter your brand name"
        />
        {errors.brand && (
          <p className="text-red-500 text-xs mt-1">{errors.brand}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-black mb-2">
          Facebook URL
        </label>
        <input
          type="text"
          name="brands_fb_url"
          value={formData.brands_fb_url}
          onChange={handleChange}
          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-1 ${
            errors.brands_fb_url
              ? "border-red-500 ring-red-500"
              : "border-gray-300 focus:border-black focus:ring-black"
          }`}
          placeholder="https://www.facebook.com/yourbrand"
        />
        {errors.brands_fb_url && (
          <p className="text-red-500 text-xs mt-1">{errors.brands_fb_url}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-black mb-2">
          Instagram Username
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">
            @
          </span>
          <input
            type="text"
            name="brands_ig_username"
            value={formData.brands_ig_username}
            onChange={handleChange}
            className={`w-full pl-8 pr-4 py-3 border rounded-md focus:outline-none focus:ring-1 ${
              errors.brands_ig_username
                ? "border-red-500 ring-red-500"
                : "border-gray-300 focus:border-black focus:ring-black"
            }`}
            placeholder="yourbrandusername"
          />
        </div>
        {errors.brands_ig_username && (
          <p className="text-red-500 text-xs mt-1">
            {errors.brands_ig_username}
          </p>
        )}
      </div>

      <div className="flex gap-3 pt-6 border-t">
        <button
          onClick={() => setIsOpen(false)}
          disabled={isUpdating}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleUpdate}
          disabled={isUpdating}
          className="flex-1 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 flex items-center justify-center disabled:opacity-50"
        >
          {isUpdating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Updating...
            </>
          ) : (
            "Update Brand Details"
          )}
        </button>
      </div>
    </div>
  );
};

export default BrandDetailsModal;
